import { shade, contrastColor } from '@mentimeter/ragnar-colors';
import { d3 } from './d3';

const PIN_SIZE = 11;
const PIN_FONT_SIZE = 10;
const TRANSITION_DURATION = 700;
const INCORRECT_OPACITY = 0.35;
const INCORRECT_AREA_OPACITY = 0.6;
const INCORRECT_AREA_COLOR = '#222';
const DEFAULT_TRANSITION_TIME = 1500;
const OPACITY_TRANSITION_TIME = 500;
const ELASTIC_TRANSITION_PERIOD = 1.2;
const FULL_AREA = [
  [0, 0],
  [0, 1],
  [1, 1],
  [1, 0],
  [0, 0],
];

export interface PinOnImageResultT {
  percentage_x: number;
  percentage_y: number;
  number_of_pins: number;
  is_correct: boolean;
}

interface Size {
  width: number;
  height: number;
}

interface T {
  selector: string;
  results: PinOnImageResultT[];
  imageSize: Size;
  color: string;
  textColor: string;
  showCorrect: boolean;
  prevShowCorrect: boolean;
  showCorrectEnabled: boolean;
  correctArea: number[][] | undefined | null;
  animationMultiplier: 0 | 1;
}

/**
 * PinOnImage: visualization that places pins on an image and can reveal a correct area
 */
export class PinOnImage implements T {
  readonly selector: string;
  readonly results: PinOnImageResultT[];
  readonly imageSize: Size;
  readonly color: string;
  readonly textColor: string;
  readonly showCorrect: boolean;
  readonly prevShowCorrect: boolean;
  readonly showCorrectEnabled: boolean;
  readonly correctArea: number[][] | undefined | null;
  readonly animationMultiplier: 0 | 1;

  /**
   * @param selector: string selection to a dom node, e.g., #pinonimage-${questionId}
   * @param results: result data that can be used to pin specific points on an image
   * @param imageSize: object to represent the width and height of the image
   * @param color: color string, e.g., #e8e8e8
   * @param textColor: color string, e.g., #e8e8e8
   * @param showCorrect: boolean that decides whether the correct answer should be revealed
   * @param prevShowCorrect: boolean, previous state of showCorrect
   * @param showCorrectEnabled: boolean that says whether the correct area feature is enabled
   * @param correctArea: Array of two-element arrays containing numbers, e.g. [[0.5, 1], ...]
   * @param animationMultiplier: number, value to multiply animation durations by, 1 (enabled) or 0 (disabled)
   */
  constructor(props: T) {
    this.selector = props.selector;
    this.results = props.results;
    this.imageSize = props.imageSize;
    this.color = props.color;
    this.textColor = props.textColor;
    this.showCorrect = props.showCorrect;
    this.prevShowCorrect = props.prevShowCorrect;
    this.showCorrectEnabled = props.showCorrectEnabled;
    this.correctArea = props.correctArea;
    this.animationMultiplier = props.animationMultiplier;
  }

  /**
   * Computes a position on the image based on its size
   *
   * @param x: number, horizontal position on the image expressed as a percentage
   * @param y: number, vertical position on the image expressed as a percentage
   * @returns string, css translation function based on x and y params
   */
  private translatePosition = (x: number, y: number): string => {
    return `translate(${x * this.imageSize.width}, ${
      y * this.imageSize.height
    })`;
  };

  /**
   * Calculates the size of a pin based on the total pins and available space
   *
   * @param numberOfPins: number, total pins in a cluster
   * @returns number, size of the pin based on number of clustered pins
   */
  private getPinSize = (numberOfPins: number): number => {
    const numPins = numberOfPins || 1;
    // log10(15) = 1.18, gradual growth from there
    // log10(1billion) = 9
    return PIN_SIZE * Math.log10(15 * numPins);
  };

  /**
   * Determines a pin color based on size of the cluster
   *
   * @param numberOfPins: number, total pins in a cluster
   * @returns string, hex color that is darker when there are more pins in a cluster
   */
  private getPinColor = (numberOfPins: number): string => {
    const numPins = numberOfPins || 1;
    const numPinsList = this.results.map((r) => r.number_of_pins || 1);
    const maxNumPins = Math.max(...numPinsList);
    const percentile = numPins / maxNumPins;

    if (percentile < 0.5) {
      return this.color;
    }
    const mixColor = 'black';
    const mixValue = percentile - 0.5;
    return shade(this.color, mixColor, mixValue);
  };

  /**
   * Chooses a high-contrast text color for the pin cluster count
   *
   * @param numberOfPins: number, total pins in a cluster
   * @returns string, hex color that is good contrast for its pin background
   */
  private getTextColor = (numberOfPins: number): string => {
    // Pins will only get darker
    if (this.textColor === 'white') {
      return this.textColor;
    }
    return contrastColor(this.getPinColor(numberOfPins));
  };

  /**
   * Determines the opacity for a pin based on whether its in the correct area
   *
   * @param isCorrect: boolean, tells whether a pin is within a designated correct area
   * @returns number, opacity value where 1 is in the correct area and < 1 is incorrect
   */
  private getOpacity = (isCorrect: boolean): number => {
    if (!this.showCorrect) return 1;
    if (!this.showCorrectEnabled) return 1;
    if (!isCorrect) return INCORRECT_OPACITY;
    return 1;
  };

  /**
   * Turns an array of points into a string with coordinates scaled with the image size
   *
   * @param points Array of two-element arrays, e.g. [[x,y], ..]
   * @returns String of coordiates separated by spaces, e.g. 'x,y x,y ...'
   */
  private getPolygonPoints = (points: number[][]): string => {
    return points
      .map(
        (d) =>
          `${(d[0] ?? 0) * this.imageSize.width},${(d[1] ?? 0) * this.imageSize.height}`,
      )
      .join(' ');
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Must resolve animations before other chained commands can be executed, like taking a screenshot with PhantomJS
   *
   * @returns array of methods that return promises (animations)
   */
  public update = (): Promise<void[]> => {
    const chart = d3.select(this.selector);

    const transitions = [];
    // Select
    const pins = chart
      .selectAll<SVGGElement, PinOnImageResultT>('.pin')
      .data(this.results, (d: PinOnImageResultT) => d.percentage_x);
    // Enter
    const pinsEnter = pins
      .enter()
      .append('g')
      .attr('class', 'pin')
      .on('mouseover', function () {
        d3.select(this)
          .attr('fill-opacity', 0)
          .select('.pin-text')
          .attr('opacity', 0);
      })
      .on('mouseout', function () {
        d3.select(this)
          .attr('fill-opacity', 1)
          .select('.pin-text')
          .attr('opacity', 1);
      })
      .attr('opacity', 0);
    const circles = pinsEnter
      .append('circle')
      .attr('class', 'pin-circle')
      .attr('stroke', this.textColor)
      .attr('stroke-width', 1)
      .attr('r', 0)
      .attr('fill', this.color);
    const texts = pinsEnter
      .append('text')
      .attr('class', 'pin-text')
      .text('')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', this.textColor)
      .attr('font-size', PIN_FONT_SIZE)
      .attr('font-weight', 600);
    // Update
    const pinsTransition = pins
      .select<SVGGElement>('.pin')
      .merge(pinsEnter)
      .attr('transform', (d) =>
        this.translatePosition(d.percentage_x, d.percentage_y),
      )
      .transition()
      .ease(d3.easeElastic)
      .duration(TRANSITION_DURATION)
      .attr('opacity', (d) => this.getOpacity(d.is_correct))
      .end();
    const circlesTransition = pins
      .select<SVGCircleElement>('.pin-circle')
      .merge(circles)
      .transition()
      .ease(d3.easeElastic)
      .duration(TRANSITION_DURATION)
      .attr('stroke', (d) => this.getTextColor(d.number_of_pins))
      .attr('r', (d) => this.getPinSize(d.number_of_pins))
      .attr('fill', (d) => this.getPinColor(d.number_of_pins))
      .end();
    const textsTransition = pins
      .select<SVGTextElement>('.pin-text')
      .merge(texts)
      .text((d) =>
        d.number_of_pins && d.number_of_pins > 1 ? d.number_of_pins : '',
      )
      .transition()
      .ease(d3.easeElastic)
      .duration(TRANSITION_DURATION)
      .attr('fill', (d) => this.getTextColor(d.number_of_pins))
      .attr('font-size', (d) => this.getPinSize(d.number_of_pins))
      .end();
    // Toggle showCorrect with enter
    pins
      .filter((d) => !d.is_correct)
      .attr(
        'opacity',
        this.showCorrect && this.showCorrectEnabled ? INCORRECT_OPACITY : 1,
      );
    // Exit
    pins.exit().transition().attr('opacity', 0).remove();

    transitions.push(pinsTransition, circlesTransition, textsTransition);

    // Correct area overlay
    if (this.showCorrectEnabled && this.correctArea) {
      const shouldAnimate = this.showCorrect !== this.prevShowCorrect;
      const correctAreaPoints = this.getPolygonPoints(this.correctArea);
      const fullAreaPoints = this.getPolygonPoints(FULL_AREA);

      const mask = chart
        .selectAll('#svg-mask')
        .data([''])
        .join('mask')
        .attr('id', 'svg-mask');

      // Everything under a white pixel will be visible
      mask
        .selectAll('.mask-incorrect-rect')
        .data([''])
        .join('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('class', 'mask-incorrect-rect')
        .attr('width', this.imageSize.width)
        .attr('height', this.imageSize.height)
        .attr('fill', 'white');

      // Everything under a black pixel will be invisible
      const correctArea = mask
        .selectAll('.mask-correct-polygon')
        .data([''])
        .join('polygon')
        .attr('class', 'mask-correct-polygon')
        .attr('fill', 'black')
        .attr(
          'points',
          !shouldAnimate && this.showCorrect
            ? correctAreaPoints
            : fullAreaPoints,
        );

      // with the mask applied, we "punch" a shape hole into the rect
      const incorrectArea = chart
        .selectAll('.incorrect-area')
        .data([''])
        .join('rect')
        .attr('class', 'incorrect-area')
        .attr('mask', 'url(#svg-mask)')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', this.imageSize.width)
        .attr('height', this.imageSize.height)
        .attr('fill', INCORRECT_AREA_COLOR)
        .style('mix-blend-mode', 'multiply')
        .attr(
          'opacity',
          this.showCorrect && !shouldAnimate ? INCORRECT_AREA_OPACITY : 0,
        );

      if (shouldAnimate) {
        const incorrectAreaTransition = incorrectArea
          .transition()
          .duration(
            this.showCorrect
              ? OPACITY_TRANSITION_TIME * this.animationMultiplier
              : 0,
          )
          .ease(d3.easeLinear)
          .attr('opacity', this.showCorrect ? INCORRECT_AREA_OPACITY : 0)
          .end();

        const correctAreaTransition = correctArea
          .transition()
          .duration(
            this.showCorrect
              ? DEFAULT_TRANSITION_TIME * this.animationMultiplier
              : 0,
          )
          .ease(d3.easeElastic.period(ELASTIC_TRANSITION_PERIOD))
          .attr('points', this.showCorrect ? correctAreaPoints : fullAreaPoints)
          .end();

        transitions.push(incorrectAreaTransition, correctAreaTransition);
      }
    }

    return Promise.all(transitions);
  };
}
